import { Button, Checkbox } from '@sixfold/common-ui';
import { omit } from '@sixfold/typed-primitives';
import React, { useState } from 'react';
import XMLViewer from 'react-xml-viewer';
import JsonView from 'react18-json-view';
import 'react18-json-view/src/style.css';
import formatXML from 'xml-formatter';

import { FormattedDate } from '../../components/date_formatting/formatted_date';
import { DiffViewerModal } from '../../components/diff_modal/diff_modal';
import { RawExternalEvents, Tour } from '../entities';

import styles from './tour_external_event_logs_list.module.css';

const isXML = (str: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(str, 'application/xml');
  return doc.getElementsByTagName('parsererror').length === 0;
};

const RawLogComponent: React.FunctionComponent<{
  log: object | string;
  headerTitle: string;
  createdAt: string;
  content?: string;
  requestPayload?: string;
  responsePayload?: string;
  showCheckbox?: boolean;
  isChecked?: boolean;
  onCheckboxChange?: (checked: boolean) => void;
  isCheckboxDisabled?: boolean;
}> = ({
  log,
  headerTitle,
  createdAt,
  content,
  requestPayload,
  responsePayload,
  showCheckbox,
  isChecked,
  onCheckboxChange,
  isCheckboxDisabled,
}) => {
  const [showsPayload, setShowsPayload] = useState<boolean>(false);
  const renderPayload = (payload: string) => {
    if (isXML(payload)) {
      return <XMLViewer xml={payload} collapsible />;
    } else {
      return <JsonView src={JSON.parse(payload)} enableClipboard={true} />;
    }
  };
  return (
    <table className="ui-table compact-table">
      <thead>
        <tr>
          <th>
            <div className={styles.headerCellContent}>
              <div className={styles.headerCellLeft}>
                {showCheckbox && (
                  <Checkbox
                    label=""
                    hideLabel
                    checked={isChecked}
                    onChange={({ checked }) => onCheckboxChange?.(!!checked)}
                    disabled={isCheckboxDisabled}
                  />
                )}
                <span>
                  {`${headerTitle} `}
                  <FormattedDate date={createdAt} />
                </span>
              </div>
              <Button size="small" onClick={() => setShowsPayload(!showsPayload)}>
                {showsPayload ? 'Hide payload' : 'Show payload'}
              </Button>
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        {showsPayload && (
          <tr>
            <td>
              <div className={styles.payloadContainer}>
                <JsonView src={log as object} enableClipboard={true} />
              </div>
            </td>
          </tr>
        )}
        {showsPayload && content !== undefined && (
          <tr>
            <td>
              <div className={styles.payloadContainer}>
                {'content:\n'}
                <JsonView src={JSON.parse(content)} enableClipboard={true} />
              </div>
            </td>
          </tr>
        )}
        {showsPayload && requestPayload !== undefined && (
          <tr>
            <td>
              <div className={styles.payloadContainer}>
                {'requestPayload:\n'}
                {renderPayload(requestPayload)}
              </div>
            </td>
          </tr>
        )}
        {showsPayload && responsePayload !== undefined && (
          <tr>
            <td>
              <div className={styles.payloadContainer}>
                {'responsePayload:\n'}
                {renderPayload(responsePayload)}
              </div>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

type TrackingStateRow = {
  externalId: string | null | undefined;
  provider: string | null | undefined;
  status: string | null | undefined;
  statusReason: string | null | undefined;
  updatedAt: string | null | undefined;
};

const ExternalTrackingStatusTable: React.FunctionComponent<{ trackingStates: TrackingStateRow[] }> = ({
  trackingStates,
}) => (
  <table className="ui-table compact-table">
    <thead>
      <tr>
        <th>External id</th>
        <th>Provider</th>
        <th>Status</th>
        <th>Status reason</th>
        <th>Last update</th>
      </tr>
    </thead>
    <tbody>
      {trackingStates.length === 0 ? (
        <tr style={{ textAlign: 'center' }}>
          <td colSpan={5}>No external tracking status</td>
        </tr>
      ) : (
        trackingStates.map((row, idx) => (
          <tr key={idx}>
            <td>{row.externalId ?? '-'}</td>
            <td>{row.provider ?? '-'}</td>
            <td>{row.status ?? '-'}</td>
            <td>{row.statusReason ?? '-'}</td>
            <td>
              <FormattedDate date={row.updatedAt} />
            </td>
          </tr>
        ))
      )}
    </tbody>
  </table>
);

export const TourExternalEventLogList: React.FunctionComponent<{
  tour: Pick<Tour, 'rawExternalEvents'>;
}> = ({ tour: { rawExternalEvents } }) => {
  const [selectedTrackingRequests, setSelectedTrackingRequests] = useState<Set<number>>(new Set());
  const [selectedExternalLogs, setSelectedExternalLogs] = useState<Set<number>>(new Set());
  const [selectedDemurrageDetentionLogs, setSelectedDemurrageDetentionLogs] = useState<Set<number>>(new Set());

  const NoEvents = (
    <>
      <h3>External tracking</h3>
      <p>No external tracking</p>
    </>
  );
  if (!rawExternalEvents) {
    return NoEvents;
  }

  const {
    trackingStates,
    trackingRequestsLogs,
    externalTrackingLogs,
    tourDemurrageDetentionLogs,
    logitoneRoadStatusCallback,
  } = formatAndExtractValues(rawExternalEvents);

  if (
    trackingStates.length === 0 &&
    trackingRequestsLogs.length === 0 &&
    externalTrackingLogs.length === 0 &&
    tourDemurrageDetentionLogs.length === 0 &&
    !logitoneRoadStatusCallback
  ) {
    return NoEvents;
  }

  const handleTrackingRequestSelection = (index: number, checked: boolean) => {
    const newSelected = new Set(selectedTrackingRequests);
    if (checked) {
      newSelected.add(index);
    } else {
      newSelected.delete(index);
    }
    setSelectedTrackingRequests(newSelected);
  };

  const handleExternalLogSelection = (index: number, checked: boolean) => {
    const newSelected = new Set(selectedExternalLogs);
    if (checked) {
      newSelected.add(index);
    } else {
      newSelected.delete(index);
    }
    setSelectedExternalLogs(newSelected);
  };

  const handleDemurrageDetentionLogSelection = (index: number, checked: boolean) => {
    const newSelected = new Set(selectedDemurrageDetentionLogs);
    if (checked) {
      newSelected.add(index);
    } else {
      newSelected.delete(index);
    }
    setSelectedDemurrageDetentionLogs(newSelected);
  };

  const getCompareData = (
    logs: Array<{
      requestPayload?: string;
      content?: string;
    }>,
    selectedIndices: Set<number>,
  ) => {
    const selectedLogs = Array.from(selectedIndices).map((index) => logs[index]);
    if (selectedLogs.length === 2) {
      return {
        oldValue: selectedLogs[0].requestPayload || selectedLogs[0].content || '',
        newValue: selectedLogs[1].requestPayload || selectedLogs[1].content || '',
      };
    }
    return { oldValue: undefined, newValue: undefined };
  };

  const trackingRequestsCompareData = getCompareData(trackingRequestsLogs, selectedTrackingRequests);
  const externalLogsCompareData = getCompareData(externalTrackingLogs, selectedExternalLogs);
  const demurrageDetentionLogsCompareData = getCompareData(tourDemurrageDetentionLogs, selectedDemurrageDetentionLogs);

  const trackingStateData: TrackingStateRow[] = trackingStates.map((st) => ({
    externalId: st.externalId,
    provider: st.provider,
    status: st.status,
    statusReason: st.statusReason,
    updatedAt: st.updatedAt,
  }));

  return (
    <>
      <h3>External tracking</h3>

      <h4>External tracking status</h4>
      <ExternalTrackingStatusTable trackingStates={trackingStateData} />

      <h4 className={styles.sectionHeader}>
        Tracking requests to external tracker
        {trackingRequestsLogs.length > 1 && (
          <div className={styles.compareButtonContainer}>
            <DiffViewerModal
              dataKey="tracking-requests-compare"
              isCompareButtonDisabled={selectedTrackingRequests.size !== 2}
              showDiffViewer={selectedTrackingRequests.size === 2}
              oldValue={trackingRequestsCompareData.oldValue}
              newValue={trackingRequestsCompareData.newValue}
              leftTitle="First Selected Request"
              rightTitle="Second Selected Request"
            />
          </div>
        )}
      </h4>
      <div className={styles.trackingRequestsSection}>
        {trackingRequestsLogs.length === 0 ? (
          <p>No tracking requests</p>
        ) : (
          <div className={styles.logSectionContent}>
            {trackingRequestsLogs.map((req, index) => (
              <div key={`${req.createdAt}-${req.trackingRequestId}-${req.responseStatus}`} className={styles.logItem}>
                <RawLogComponent
                  log={omit(req, 'requestPayload', 'responsePayload', '__typename')}
                  requestPayload={req.requestPayload}
                  responsePayload={req.responsePayload}
                  headerTitle={'External tracking request log at'}
                  createdAt={req.createdAt}
                  showCheckbox={trackingRequestsLogs.length > 1}
                  isChecked={selectedTrackingRequests.has(index)}
                  onCheckboxChange={(checked) => handleTrackingRequestSelection(index, checked)}
                  isCheckboxDisabled={!selectedTrackingRequests.has(index) && selectedTrackingRequests.size >= 2}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <h4 className={styles.sectionHeader}>
        Tracking input from external tracker (10 latest)
        {externalTrackingLogs.length > 1 && (
          <div className={styles.compareButtonContainer}>
            <DiffViewerModal
              dataKey="external-logs-compare"
              isCompareButtonDisabled={selectedExternalLogs.size !== 2}
              showDiffViewer={selectedExternalLogs.size === 2}
              oldValue={externalLogsCompareData.oldValue}
              newValue={externalLogsCompareData.newValue}
              leftTitle="First Selected Log"
              rightTitle="Second Selected Log"
            />
          </div>
        )}
      </h4>
      <div className={styles.externalLogsSection}>
        {externalTrackingLogs.length === 0 ? (
          <p>No external tracking logs</p>
        ) : (
          <div className={styles.logSectionContent}>
            {externalTrackingLogs.map((log, index) => (
              <div key={`${log.createdAt}-${log.contentType}`} className={styles.logItem}>
                <RawLogComponent
                  log={omit(log, 'content', '__typename')}
                  content={log.content}
                  headerTitle={`Tour external tracking log from ${log.provider} at`}
                  createdAt={log.createdAt}
                  showCheckbox={externalTrackingLogs.length > 1}
                  isChecked={selectedExternalLogs.has(index)}
                  onCheckboxChange={(checked) => handleExternalLogSelection(index, checked)}
                  isCheckboxDisabled={!selectedExternalLogs.has(index) && selectedExternalLogs.size >= 2}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <h4 className={styles.sectionHeader}>
        Demurrage and detention input from external tracker (10 latest)
        {tourDemurrageDetentionLogs.length > 1 && (
          <div className={styles.compareButtonContainer}>
            <DiffViewerModal
              dataKey="demurrage-detention-compare"
              isCompareButtonDisabled={selectedDemurrageDetentionLogs.size !== 2}
              showDiffViewer={selectedDemurrageDetentionLogs.size === 2}
              oldValue={demurrageDetentionLogsCompareData.oldValue}
              newValue={demurrageDetentionLogsCompareData.newValue}
              leftTitle="First Selected Log"
              rightTitle="Second Selected Log"
            />
          </div>
        )}
      </h4>
      <div className={styles.demurrageDetentionSection}>
        {tourDemurrageDetentionLogs.length === 0 ? (
          <p>No demurrage and detention logs</p>
        ) : (
          <div className={styles.logSectionContent}>
            {tourDemurrageDetentionLogs.map((dd, index) => (
              <div key={`${dd.createdAt}-${dd.demurrageDetentionLogId}`} className={styles.logItem}>
                <RawLogComponent
                  log={omit(dd, 'content', '__typename')}
                  content={dd.content}
                  headerTitle={'Demurrage and detention log at'}
                  createdAt={dd.createdAt}
                  showCheckbox={tourDemurrageDetentionLogs.length > 1}
                  isChecked={selectedDemurrageDetentionLogs.has(index)}
                  onCheckboxChange={(checked) => handleDemurrageDetentionLogSelection(index, checked)}
                  isCheckboxDisabled={
                    !selectedDemurrageDetentionLogs.has(index) && selectedDemurrageDetentionLogs.size >= 2
                  }
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {logitoneRoadStatusCallback && (
        <>
          <h4>Logitone Road Status Callback</h4>
          <RawLogComponent
            key={`${logitoneRoadStatusCallback.updatedAt}-${logitoneRoadStatusCallback.tourId}`}
            log={omit(logitoneRoadStatusCallback, 'payload')}
            content={logitoneRoadStatusCallback.payload}
            headerTitle={'Logitone road status callback at'}
            createdAt={logitoneRoadStatusCallback.updatedAt}
          />
        </>
      )}
    </>
  );
};
// As some of the values received as string are in fact JSON.stringified we need to parse them so the fields won't be double stringified.
// Some fields contain raw XML payloads that require different handling
function formatAndExtractValues(rawEvents: RawExternalEvents) {
  const trackingRequestsLogs = rawEvents.tourExternalTrackingRequests.map((req) => {
    const requestPayload = parseJSONorXML(req.requestPayload);
    const responsePayload = req.responsePayload !== null ? parseJSONorXML(req.responsePayload) : undefined;
    const responseStatus = req.responseStatus ?? undefined;
    return {
      ...req,
      requestPayload,
      responsePayload,
      responseStatus,
    };
  });
  const externalTrackingLogs = rawEvents.tourExternalTrackingLogs.map((log) => {
    const content = parseJSONorXML(log.content);
    return {
      ...log,
      content,
    };
  });

  const tourDemurrageDetentionLogs = rawEvents.tourDemurrageDetentionLogs.map((dd) => {
    const content = parseJSONorXML(dd.content);
    return {
      ...dd,
      content,
    };
  });
  return {
    trackingStates: rawEvents.tourExternalTracking,
    trackingRequestsLogs,
    externalTrackingLogs,
    tourDemurrageDetentionLogs,
    logitoneRoadStatusCallback: rawEvents.logitoneRoadStatusCallback,
  };
}

function parseJSONorXML(stringToParse: string): string {
  let parsedInput;
  try {
    parsedInput = JSON.stringify(JSON.parse(stringToParse), null, 2);
  } catch (_e) {
    try {
      parsedInput = formatXML(stringToParse, { indentation: '  ' });
    } catch (_e) {
      parsedInput = 'Could not parse neither JSON or XML.';
    }
  }
  return parsedInput;
}
